import React, { useCallback } from "react";
import styled from "styled-components";
import { z } from "zod";
import { useAuthState } from "@riotgames/riotbar-react";
import { Blade } from "../../../components/Blade";
import { mixinSkins } from "../../../skins/shared/mixins/themeStyles";
import { SkinId } from "../../../skins/types";
import { riotProductSkin } from "../skins/riot-product";
import { Backdrop, BackdropBackground } from "../../../components/Backdrop";
import { sendEvent } from "../../../functional/analyticsUtils";
import { Media, MediaImage } from "../../../components/Media";
import { BladeContent } from "../../../components/BladeContent";
import { BladeHeader, BladeHeaderBody, BladeHeaderSupertitle, BladeHeaderTitle } from "../../../components/BladeHeader";
import { RichText } from "../../../components/RichText";
import { CTA } from "../../../components/CTA";
import { ProductMastheadBladeSchema } from "../../../schemas/blades/ProductMasthead";
import { useAnalytics } from "../../../providers/AnalyticsProvider";
import { valorantSkin } from "../skins/valorant";
export const PlatformSelectionMastheadBladeContractSchema = ProductMastheadBladeSchema.extend({
    type: z.literal("platformSelectionMasthead"),
});
const _PlatformSelectionMasthead = (props) => {
    const { isAuthenticated } = useAuthState();
    const { backdrop, logo, header, availability, linksLabel, className, ...bladeProps } = props;
    const { page, blade, uiLocation } = useAnalytics();
    const handleLinkClick = useCallback((event) => {
        const target = event.target;
        const link = target.closest("a");
        if (link) {
            event.preventDefault();
            const href = link.getAttribute("href") || "";
            const linkText = link.textContent || "";
            const contentAnalytics = {
                id: href,
                title: linkText,
                uiType: "link",
                action: { type: "weblink", payload: { url: href } },
            };
            sendEvent("Create_Riot_Account", { page, blade, uiLocation, content: contentAnalytics });
            window.location.href = href;
        }
    }, [page, blade, uiLocation]);
    const handleCTAEvents = useCallback((iconLink) => {
        const { action, title } = iconLink.link;
        const ctaEvent = `Download_${iconLink.link.title}`;
        const contentAnalytics = {
            id: ctaEvent,
            title,
            uiType: "link",
            action,
        };
        sendEvent(ctaEvent, { page, blade, uiLocation, content: contentAnalytics });
    }, [page, blade, uiLocation]);
    return (React.createElement(Blade, { "data-testid": "platform-selection-masthead", className: `platform-selection-masthead ${className || ""}`, ...bladeProps },
        backdrop ? (React.createElement(Backdrop, null, backdrop.background && (React.createElement(BackdropBackground, null,
            React.createElement(Media, { media: backdrop.background, autoPlay: true }))))) : null,
        React.createElement("div", { className: "gradient-container" }),
        React.createElement(BladeContent, { width: "large", className: "platform-selection-masthead-content" },
            logo?.image ? (React.createElement("h1", { "data-testid": "header-logo", className: "platform-selection-masthead-logo" }, logo?.action?.type === "weblink" ? (React.createElement("a", { href: logo?.action.payload.url, title: logo.image?.accessibilityText, tabIndex: 0 },
                React.createElement(MediaImage, { media: logo?.image, optimized: true, fit: "height", "data-testid": "platform-selection-masthead-logo-img" }))) : (React.createElement(MediaImage, { media: logo?.image, optimized: true, fit: "height", "data-testid": "platform-selection-masthead-logo-img" })))) : null,
            header ? (React.createElement(BladeHeader, null,
                header.subtitle && (React.createElement(BladeHeaderSupertitle, { "data-testid": "platform-selection-masthead-header-supertitle" }, header.subtitle)),
                React.createElement(BladeHeaderTitle, { "data-testid": "platform-selection-masthead-header-title" }, header.title),
                header.description && (React.createElement(BladeHeaderBody, { "data-testid": "platform-selection-masthead-header-body", onClick: handleLinkClick },
                    React.createElement(RichText, { ...header.description }))))) : null,
            availability?.links ? (React.createElement("div", { "data-testid": "platform-selection-masthead-ctas", className: "platform-selection-masthead-ctas" }, availability.links?.map((ctaLink, i) => (React.createElement("div", { "data-testid": `platform-selection-masthead-cta-${ctaLink.iconName}`, key: i },
                React.createElement(CTA, { icon: ctaLink.iconName, variant: "primary", ...ctaLink.link, onClick: () => handleCTAEvents(ctaLink) }, ctaLink.link.title)))))) : null,
            linksLabel && !isAuthenticated ? (React.createElement("div", { className: "platform-selection-masthead-link-label", "data-testid": "platform-selection-masthead-label", onClick: handleLinkClick },
                React.createElement(RichText, { ...linksLabel }))) : null)));
};
export const PlatformSelectionMasthead = styled(_PlatformSelectionMasthead) `
  ${mixinSkins({
    [SkinId.RiotProduct]: riotProductSkin,
    [SkinId.Valorant]: valorantSkin,
})}
`;
//# sourceMappingURL=PlatformSelectionMasthead.js.map